import { AcceptHosted } from 'react-acceptjs';
import { useEffect } from 'react';
import LoadingIndicator from '3-components/LoadingIndicator/LoadingIndicator';
import useAuthorizeUpdate from './useAuthorizeUpdate';
import InlineMessage from '3-components/InlineMessage/InlineMessage';
import { useAuthorizeContext } from './useAuthorizeContext';
import { useDonationBlockProps } from '../DonationBlockProvider';
import queryString from 'query-string';
import { useDonationStore } from '../libs/useDonationStore';
import { pushDataLayerOnFormStepComplete } from '3-components/Tracker/Tracker';
import { DONATION_FORM_NAME } from '../libs/common';

interface AuthorizeTransactionResponse {
    accountType: string;
    accountNumber: string;
    transId: string;
    responseCode: string;
    authorization: string;
    billTo: {
        phoneNumber: string;
        firstName: string;
        lastName: string;
        city: string;
        state: string;
        zip: string;
        country: string;
    };
    totalAmount: string;
    dateTime: string;
}

const LoadingVerifyPayment = (): JSX.Element => {
    return (
        <div className="loading-payment-wrapper">
            <LoadingIndicator
                loadingText={`Please wait, we are verifying your payment`}
            />
        </div>
    );
};

const AuthorizePayment = (): JSX.Element => {
    const { paymentData: { token: paymentToken, submissionId, sandbox } } = useAuthorizeContext();
    const { thankYouPageUrl, cancelPageUrl } = useDonationBlockProps();
    const { donationData } = useDonationStore();

    useEffect(() => {
        return () => {
            window.AuthorizeNetIFrame = undefined;
        };
    }, []);

    const hideInitLoadingScreen = (): void => {
        const elWrapper = document.querySelector('.fhf-authorize-payment-wrapper');

        if (elWrapper) {
            elWrapper.classList.remove('is-loading');
        }
    };

    const resizeIframe = (height: number): void => {
        hideInitLoadingScreen();

        const elIframe: HTMLIFrameElement | null = document.querySelector('.fhf-authorize-iframe');

        if (elIframe) {
            elIframe.style.height = `${height}px`;
        }
    };

    const { isLoading: isVerifyingPayment, isSuccess: isVerified, isError, message, finalizePayment } = useAuthorizeUpdate();

    useEffect(() => {
        const form = document.getElementById('formAuthorizeNetPopup') as HTMLFormElement;
        const isFormExists = typeof form !== 'undefined' && form !== null;

        if (isFormExists) {
            form.submit();
        }
    }, [paymentToken]);

    useEffect(() => {
        // redirect after successfuly verifying payment
        if (!isVerifyingPayment && isVerified) {
            if (thankYouPageUrl) {
                const thankYouUrl = queryString.stringifyUrl({
                    url: thankYouPageUrl,
                    query: { donationKey: donationData.submissionKey },
                });

                window.location.href = thankYouUrl;
            }
        }
    }, [isVerifyingPayment, isVerified, thankYouPageUrl, donationData.submissionKey]);

    if (!paymentToken) {
        return (
            <div>
                <p>Error initialize payment iframe. Error: Missing payment token</p>
            </div>
        );
    }

    return (
        <div className="fhf-authorize-payment-wrapper is-loading">
            <div className="authorize-loading-screen">
                <LoadingIndicator
                    loadingText={`Initializing payment form`}
                />
            </div>

            {(isVerifyingPayment && !isVerified) && (
                <LoadingVerifyPayment />
            )}

            <AcceptHosted
                formToken={paymentToken as string}
                integration="iframe"
                environment={sandbox ? 'SANDBOX' : 'PRODUCTION'}
                onTransactionResponse={(response: unknown): void => {
                    const transactionResponse = response as AuthorizeTransactionResponse;

                    if (transactionResponse.responseCode === '1') {
                        // GA4 dataLayer => step complete
                        pushDataLayerOnFormStepComplete({
                            formName: DONATION_FORM_NAME,
                            formType: 'donation',
                            stepName: 'payment',
                        });

                        finalizePayment({
                            submissionId,
                            transactionId: transactionResponse.transId,
                        });
                    }
                }}
                onResize={(width, height): void => {
                    if (height) {
                        resizeIframe(height);
                    }
                } }
                onCancel={(): void => {
                    window.location.href = cancelPageUrl as string;
                }}
                onSuccessfulSave={(): void => {
                    console.info('on successful save');
                }}
            >
                <AcceptHosted.Button className="btn btn-primary">
                    Submit Token
                </AcceptHosted.Button>
                <AcceptHosted.IFrame className="fhf-authorize-iframe" />
            </AcceptHosted>

            {isError && message && (
                <InlineMessage
                    status="error"
                >
                    Can not verify your payment. Please contact administrator
                </InlineMessage>
            )}
        </div>
    );
};

export default AuthorizePayment;
